import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import {
  Plus,
  X,
  Search,
  Loader2,
  ArrowLeft,
  Users,
  MapPinned,
  Trash2,
  ListX,
  Check,
  Save,
  ArrowRightLeft,
} from "lucide-react";
import api from "../api/axios";


/* -------------------------------------------------------------------------- */
/*  Data sources:                                                             */
/*    GET  /api/admin/border-tax/three/providers   -> providers with assign_three_month_borders    */
/*                               populated (main table)                       */
/*    GET  /api/admin/border-tax/three/states      -> full state list (assign modal checkboxes)    */
/*    POST /api/admin/border-tax/three/assign-bulk { providerId, stateIds }                        */
/*    POST /api/admin/border-tax/three/remove      { providerId, stateId }                         */
/*    POST /api/admin/border-tax/three/remove-bulk { providerId, stateIds }                        */
/*    POST /api/admin/border-tax/three/remove-all  { providerId }                                  */
/* -------------------------------------------------------------------------- */

const ProviderThreeMonth = () => {
  const navigate = useNavigate();

  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const [allStates, setAllStates] = useState([]);
  const [statesLoading, setStatesLoading] = useState(false);

  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [activeProvider, setActiveProvider] = useState(null);
  const [selectedStateIds, setSelectedStateIds] = useState([]);
  const [stateSearch, setStateSearch] = useState("");
  const [saving, setSaving] = useState(false);

  const [rowBusyId, setRowBusyId] = useState(null);

  // --- Transfer All (new) ---
  const [transferModalOpen, setTransferModalOpen] = useState(false);
  const [transferSource, setTransferSource] = useState(null);
  const [transferTargetId, setTransferTargetId] = useState("");
  const [transferSearch, setTransferSearch] = useState("");
  const [transferring, setTransferring] = useState(false);

  /* -------------------------------- fetch -------------------------------- */

  const fetchProviders = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/border-tax/three/providers");
      setProviders(Array.isArray(data?.data) ? data.data : []);
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Could not load providers",
        text: err?.response?.data?.message || "Something went wrong while fetching providers.",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchAllStates = async () => {
    setStatesLoading(true);
    try {
      const { data } = await api.get("/admin/border-tax/three/states");
      setAllStates(Array.isArray(data?.data) ? data.data : []);
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Could not load states",
        text: err?.response?.data?.message || "Something went wrong while fetching states.",
      });
    } finally {
      setStatesLoading(false);
    }
  };

  useEffect(() => {
    fetchProviders();
    fetchAllStates();
  }, []);

  /* --------------------------- helpers on borders --------------------------- */

  // assign_three_month_borders items can be populated objects ({_id, main_state})
  // or plain id strings depending on backend populate — handle both.
  const getBorderId = (b) => (typeof b === "string" ? b : b?._id);
  const getBorderName = (b) => (typeof b === "string" ? b : b?.main_state || "—");

  // Map of stateId -> providerId, built from every provider's assigned borders.
  // Used to hide a state from the assign list once it belongs to some OTHER
  // provider — prevents assigning the same state to more than one provider.
  const assignedElsewhereMap = useMemo(() => {
    const map = {};
    providers.forEach((p) => {
      (p.assign_three_month_borders || []).forEach((b) => {
        map[getBorderId(b)] = p._id;
      });
    });
    return map;
  }, [providers]);

  const isTakenByOther = (stateId) =>
    assignedElsewhereMap[stateId] && assignedElsewhereMap[stateId] !== activeProvider?._id;

  /* ------------------------------ assign modal ------------------------------ */

  const openAssignModal = (provider) => {
    setActiveProvider(provider);
    setSelectedStateIds((provider.assign_three_month_borders || []).map(getBorderId));
    setStateSearch("");
    setAssignModalOpen(true);
  };

  const closeAssignModal = () => {
    setAssignModalOpen(false);
    setActiveProvider(null);
    setSelectedStateIds([]);
  };

  const toggleStateSelection = (id) => {
    setSelectedStateIds((prev) =>
      prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]
    );
  };

  const handleSaveAssignments = async () => {
    if (!activeProvider) return;

    const originalIds = (activeProvider.assign_three_month_borders || []).map(getBorderId);
    const toAdd = selectedStateIds.filter((id) => !originalIds.includes(id));
    const toRemove = originalIds.filter((id) => !selectedStateIds.includes(id));

    if (toAdd.length === 0 && toRemove.length === 0) {
      closeAssignModal();
      return;
    }

    setSaving(true);
    try {
      // Run add + remove in parallel — independent operations.
      await Promise.all([
        toAdd.length > 0
          ? api.post("/admin/border-tax/three/assign-bulk", {
              providerId: activeProvider._id,
              stateIds: toAdd,
            })
          : Promise.resolve(),
        toRemove.length > 0
          ? api.post("/admin/border-tax/three/remove-bulk", {
              providerId: activeProvider._id,
              stateIds: toRemove,
            })
          : Promise.resolve(),
      ]);
      Swal.fire({ icon: "success", title: "States updated", timer: 1200, showConfirmButton: false });
      closeAssignModal();
      fetchProviders();
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Could not update assignments",
        text: err?.response?.data?.message || "Something went wrong.",
      });
    } finally {
      setSaving(false);
    }
  };

  /* --------------------------- quick chip removal --------------------------- */

  const handleRemoveOne = async (provider, stateId) => {
    setRowBusyId(provider._id);
    try {
      await api.post("/admin/border-tax/three/remove", {
        providerId: provider._id,
        stateId,
      });
      fetchProviders();
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Could not remove state",
        text: err?.response?.data?.message || "Something went wrong.",
      });
    } finally {
      setRowBusyId(null);
    }
  };

  const handleRemoveAll = async (provider) => {
    if (!(provider.assign_three_month_borders || []).length) return;

    const result = await Swal.fire({
      icon: "warning",
      title: `Remove all states from "${provider.name}"?`,
      showCancelButton: true,
      confirmButtonText: "Remove All",
      confirmButtonColor: "#dc2626",
      cancelButtonText: "Cancel",
    });
    if (!result.isConfirmed) return;

    setRowBusyId(provider._id);
    try {
      await api.post("/admin/border-tax/three/remove-all", { providerId: provider._id });
      fetchProviders();
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Could not remove states",
        text: err?.response?.data?.message || "Something went wrong.",
      });
    } finally {
      setRowBusyId(null);
    }
  };

  /* ------------------------------ transfer all ------------------------------ */
  // Moves every state currently assigned to `transferSource` onto the chosen
  // target provider in one shot. Uses Promise.all to fire the "assign to
  // target" and "clear from source" calls together instead of sequentially.

  const openTransferModal = (provider) => {
    if (!(provider.assign_three_month_borders || []).length) return;
    setTransferSource(provider);
    setTransferTargetId("");
    setTransferSearch("");
    setTransferModalOpen(true);
  };

  const closeTransferModal = () => {
    if (transferring) return;
    setTransferModalOpen(false);
    setTransferSource(null);
    setTransferTargetId("");
    setTransferSearch("");
  };

  const transferCandidates = useMemo(
    () =>
      providers
        .filter((p) => p._id !== transferSource?._id)
        .filter((p) => (p.name || "").toLowerCase().includes(transferSearch.toLowerCase())),
    [providers, transferSource, transferSearch]
  );

  const handleTransferAll = async () => {
    if (!transferSource || !transferTargetId) return;

    const stateIds = (transferSource.assign_three_month_borders || []).map(getBorderId);
    if (!stateIds.length) return;

    const targetProvider = providers.find((p) => p._id === transferTargetId);

    const result = await Swal.fire({
      icon: "warning",
      title: `Transfer ${stateIds.length} state(s)?`,
      text: `From "${transferSource.name}" to "${targetProvider?.name}"`,
      showCancelButton: true,
      confirmButtonText: "Transfer All",
      confirmButtonColor: "#dc2626",
      cancelButtonText: "Cancel",
    });
    if (!result.isConfirmed) return;

    setTransferring(true);
    try {
      // Fire both calls together: assign all states to the target provider
      // while clearing them from the source provider.
      await Promise.all([
        api.post("/admin/border-tax/three/assign-bulk", {
          providerId: transferTargetId,
          stateIds,
        }),
        api.post("/admin/border-tax/three/remove-all", {
          providerId: transferSource._id,
        }),
      ]);
      Swal.fire({ icon: "success", title: "States transferred", timer: 1200, showConfirmButton: false });
      closeTransferModal();
      fetchProviders();
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Could not transfer states",
        text: err?.response?.data?.message || "Something went wrong.",
      });
    } finally {
      setTransferring(false);
    }
  };

  /* --------------------------------- derived --------------------------------- */

  const filteredProviders = providers.filter((p) =>
    (p.name || "").toLowerCase().includes(search.toLowerCase()) ||
    (p.whatsapp_number || "").includes(search)
  );

  const filteredModalStates = useMemo(
    () =>
      allStates
        .filter((s) => !isTakenByOther(s._id))
        .filter((s) => (s.main_state || "").toLowerCase().includes(stateSearch.toLowerCase())),
    [allStates, stateSearch, assignedElsewhereMap, activeProvider]
  );

  const allVisibleSelected =
    filteredModalStates.length > 0 &&
    filteredModalStates.every((s) => selectedStateIds.includes(s._id));

  /* ----------------------------------------------------------------------------- */

  return (
    <div className="min-h-screen bg-gray-50 text-gray-800 p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="p-2 rounded-lg text-gray-500 hover:text-gray-900 hover:bg-gray-200/60 transition-colors"
            title="Back"
          >
            <ArrowLeft size={20} />
          </button>
          <div className="flex items-center gap-2.5">
            <div className="p-2 rounded-xl bg-red-50 text-red-600 border border-red-100">
              <Users size={22} />
            </div>
            <div>
              <h1 className="text-xl font-bold tracking-tight text-gray-900">3 Month Tax — Providers</h1>
              <p className="text-xs text-gray-500">Assign / remove states for each provider</p>
            </div>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="relative mb-5 max-w-sm">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search provider name or number..."
          className="w-full pl-9 pr-3 py-2.5 rounded-xl bg-white border border-gray-200 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-500/30 focus:border-red-400"
        />
      </div>

      {/* Table */}
      <div className="rounded-2xl border border-gray-200 bg-white overflow-hidden shadow-sm">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200 bg-gray-50 text-left text-gray-500">
              <th className="px-5 py-3 font-medium">Provider</th>
              <th className="px-5 py-3 font-medium">WhatsApp</th>
              <th className="px-5 py-3 font-medium">Assigned States</th>
              <th className="px-5 py-3 font-medium">Status</th>
              <th className="px-5 py-3 font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={5} className="px-5 py-10 text-center text-gray-400">
                  <Loader2 size={20} className="inline animate-spin mr-2" />
                  Loading providers...
                </td>
              </tr>
            ) : filteredProviders.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-5 py-10 text-center text-gray-400">
                  No providers found.
                </td>
              </tr>
            ) : (
              filteredProviders.map((provider) => {
                const borders = provider.assign_three_month_borders || [];
                const busy = rowBusyId === provider._id;

                return (
                  <tr key={provider._id} className="border-b border-gray-100 last:border-0 hover:bg-gray-50 transition-colors align-top">
                    <td className="px-5 py-3.5">
                      <span className="font-medium text-gray-900">{provider.name}</span>
                    </td>
                    <td className="px-5 py-3.5 text-gray-500">{provider.whatsapp_number}</td>
                    <td className="px-5 py-3.5">
                      {borders.length ? (
                        <div className="flex flex-wrap gap-1.5 max-w-sm">
                          {borders.map((b) => {
                            const id = getBorderId(b);
                            return (
                              <span
                                key={id}
                                className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-50 text-red-600 text-xs border border-red-100"
                              >
                                <MapPinned size={11} />
                                {getBorderName(b)}
                                <button
                                  onClick={() => handleRemoveOne(provider, id)}
                                  disabled={busy}
                                  className="ml-0.5 hover:text-red-800 disabled:opacity-50"
                                  title="Remove"
                                >
                                  <X size={12} />
                                </button>
                              </span>
                            );
                          })}
                        </div>
                      ) : (
                        <span className="text-gray-300">No states assigned</span>
                      )}
                    </td>
                    <td className="px-5 py-3.5">
                      <span
                        className={`
                          inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium
                          ${provider.active ? "bg-emerald-50 text-emerald-600 border border-emerald-100" : "bg-gray-100 text-gray-500 border border-gray-200"}
                        `}
                      >
                        <span className={`w-1.5 h-1.5 rounded-full ${provider.active ? "bg-emerald-500" : "bg-gray-400"}`} />
                        {provider.active ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td className="px-5 py-3.5">
                      <div className="flex items-center justify-end gap-1.5">
                        <button
                          onClick={() => openAssignModal(provider)}
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-red-600 hover:bg-red-50 transition-colors"
                          title="Assign states"
                        >
                          <Plus size={14} />
                          Assign
                        </button>
                        {borders.length > 0 && (
                          <button
                            onClick={() => openTransferModal(provider)}
                            disabled={busy}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-blue-600 hover:bg-blue-50 transition-colors disabled:opacity-40"
                            title="Transfer all states to another provider"
                          >
                            <ArrowRightLeft size={14} />
                            Transfer All
                          </button>
                        )}
                        <button
                          onClick={() => handleRemoveAll(provider)}
                          disabled={busy || borders.length === 0}
                          className="p-2 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors disabled:opacity-30"
                          title="Remove all states"
                        >
                          <ListX size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Assign modal */}
      {assignModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={closeAssignModal} />

          <div className="relative w-full max-w-lg rounded-2xl border border-gray-200 bg-white shadow-2xl">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
              <div>
                <h2 className="text-base font-semibold text-gray-900">Assign States</h2>
                <p className="text-xs text-gray-500">{activeProvider?.name}</p>
              </div>
              <button onClick={closeAssignModal} className="p-1.5 rounded-lg text-gray-400 hover:text-gray-800 hover:bg-gray-100 transition-colors">
                <X size={18} />
              </button>
            </div>

            <div className="px-5 py-4 space-y-3">
              <div className="relative">
                <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  value={stateSearch}
                  onChange={(e) => setStateSearch(e.target.value)}
                  placeholder="Search states..."
                  className="w-full pl-9 pr-3 py-2.5 rounded-xl bg-gray-50 border border-gray-200 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-500/30 focus:border-red-400"
                />
              </div>

              <div className="max-h-80 overflow-y-auto rounded-xl border border-gray-100 divide-y divide-gray-100">
                {statesLoading ? (
                  <div className="px-4 py-8 text-center text-gray-400">
                    <Loader2 size={18} className="inline animate-spin mr-2" />
                    Loading states...
                  </div>
                ) : filteredModalStates.length === 0 ? (
                  <div className="px-4 py-8 text-center text-gray-400">No states found.</div>
                ) : (
                  filteredModalStates.map((state) => {
                    const checked = selectedStateIds.includes(state._id);
                    return (
                      <button
                        key={state._id}
                        onClick={() => toggleStateSelection(state._id)}
                        className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-gray-50 transition-colors text-left"
                      >
                        <span className="text-sm text-gray-800">{state.main_state}</span>
                        <span
                          className={`
                            w-5 h-5 rounded-md border flex items-center justify-center transition-colors
                            ${checked ? "bg-red-600 border-red-600" : "border-gray-300"}
                          `}
                        >
                          {checked && <Check size={13} className="text-white" />}
                        </span>
                      </button>
                    );
                  })
                )}
              </div>

              <p className="text-xs text-gray-400">{selectedStateIds.length} state(s) selected</p>
            </div>

            <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-gray-200">
              <button
                onClick={closeAssignModal}
                className="px-4 py-2 rounded-xl text-sm text-gray-500 hover:text-gray-800 hover:bg-gray-100 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveAssignments}
                disabled={saving}
                className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium bg-red-600 text-white shadow-sm shadow-red-200 hover:bg-red-700 transition disabled:opacity-60"
              >
                {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Transfer All modal (new) */}
      {transferModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={closeTransferModal} />

          <div className="relative w-full max-w-lg rounded-2xl border border-gray-200 bg-white shadow-2xl">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
              <div>
                <h2 className="text-base font-semibold text-gray-900">Transfer All States</h2>
                <p className="text-xs text-gray-500">
                  Move {(transferSource?.assign_three_month_borders || []).length} state(s) from{" "}
                  <span className="font-medium text-gray-700">{transferSource?.name}</span> to another provider
                </p>
              </div>
              <button
                onClick={closeTransferModal}
                disabled={transferring}
                className="p-1.5 rounded-lg text-gray-400 hover:text-gray-800 hover:bg-gray-100 transition-colors disabled:opacity-40"
              >
                <X size={18} />
              </button>
            </div>

            <div className="px-5 py-4 space-y-3">
              <div className="relative">
                <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  value={transferSearch}
                  onChange={(e) => setTransferSearch(e.target.value)}
                  placeholder="Search target provider..."
                  className="w-full pl-9 pr-3 py-2.5 rounded-xl bg-gray-50 border border-gray-200 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400"
                />
              </div>

              <div className="max-h-80 overflow-y-auto rounded-xl border border-gray-100 divide-y divide-gray-100">
                {transferCandidates.length === 0 ? (
                  <div className="px-4 py-8 text-center text-gray-400">No other providers found.</div>
                ) : (
                  transferCandidates.map((p) => {
                    const selected = transferTargetId === p._id;
                    return (
                      <button
                        key={p._id}
                        onClick={() => setTransferTargetId(p._id)}
                        className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-gray-50 transition-colors text-left"
                      >
                        <div>
                          <span className="text-sm text-gray-800 block">{p.name}</span>
                          <span className="text-xs text-gray-400">{p.whatsapp_number}</span>
                        </div>
                        <span
                          className={`
                            w-5 h-5 rounded-full border flex items-center justify-center transition-colors
                            ${selected ? "bg-blue-600 border-blue-600" : "border-gray-300"}
                          `}
                        >
                          {selected && <Check size={13} className="text-white" />}
                        </span>
                      </button>
                    );
                  })
                )}
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-gray-200">
              <button
                onClick={closeTransferModal}
                disabled={transferring}
                className="px-4 py-2 rounded-xl text-sm text-gray-500 hover:text-gray-800 hover:bg-gray-100 transition-colors disabled:opacity-40"
              >
                Cancel
              </button>
              <button
                onClick={handleTransferAll}
                disabled={transferring || !transferTargetId}
                className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium bg-blue-600 text-white shadow-sm shadow-blue-200 hover:bg-blue-700 transition disabled:opacity-60"
              >
                {transferring ? <Loader2 size={16} className="animate-spin" /> : <ArrowRightLeft size={16} />}
                Transfer All
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProviderThreeMonth;